import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Wrench, Calendar, MapPin } from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import Contact from "./site/Contact";
import { SITE } from "../site.config";
import { BUILDS, BUILD_PATH } from "../data/builds";
import useDocumentMeta from "../hooks/useDocumentMeta";

const SITE_URL = "https://powerzautoelectrical.com.au";

export default function BuildsPage() {
  const canonical = `${SITE_URL}/builds`;

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Completed Auto Electrical Builds — Powerz Auto Electrical Sunshine Coast",
    itemListElement: BUILDS.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}${BUILD_PATH(b.slug)}`,
      name: b.title,
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Completed Builds", item: canonical },
    ],
  };

  useDocumentMeta({
    title: "Completed Jobs & Customer Builds | Powerz Auto Electrical Sunshine Coast",
    description:
      "Real auto electrical jobs completed at Powerz Auto Electrical on the Sunshine Coast — 4WD auto electrical fitouts, UHF installs, dual battery setups, Tow-Pro brake controllers and more. Photos, gear lists and writeups from our Kunda Park workshop.",
    canonical,
    image: SITE.business.logo,
    jsonLd: [itemListJsonLd, breadcrumbJsonLd],
  });

  return (
    <div className="bg-black text-white">
      <Header />

      <section className="relative bg-black pt-40 md:pt-48 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Link
            to="/"
            data-testid="builds-back-link"
            className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            HOME
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">
              REAL JOBS • SUNSHINE COAST
            </span>
          </div>

          <h1
            data-testid="builds-headline"
            className="bebas text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-5xl"
          >
            COMPLETED <br />
            <span className="text-cyan-pz glow-cyan">BUILDS &amp; INSTALLS.</span>
          </h1>

          <p className="mt-8 text-white/70 barlow text-lg md:text-xl max-w-3xl leading-relaxed">
            A growing library of real auto electrical jobs we've finished at our
            Kunda Park workshop — 4WD auto electrical fitouts, UHF installs, dual battery
            upgrades, Tow-Pro brake controllers, canopy electrical
            and more. Photos, gear lists and the full writeup of how each one
            went together.
          </p>
        </div>
      </section>

      <section className="relative bg-black pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {BUILDS.length === 0 ? (
            <p className="text-white/60 barlow">
              Builds coming soon. Check back shortly.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {BUILDS.map((b) => (
                <Link
                  key={b.slug}
                  to={BUILD_PATH(b.slug)}
                  data-testid={`build-card-${b.slug}`}
                  className="group relative block bg-[#0A0A0A] border border-white/10 hover:border-cyan-pz transition-all duration-300 overflow-hidden clip-perf"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={b.hero}
                      alt={b.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <p className="bebas text-cyan-pz tracking-[0.2em] text-xs mb-2 inline-flex items-center gap-2">
                      <Wrench className="w-3.5 h-3.5" />
                      {b.service.toUpperCase()}
                    </p>
                    <h2 className="bebas text-white text-2xl md:text-3xl leading-tight mb-3">
                      {b.title}
                    </h2>
                    <p className="text-white/60 barlow text-sm leading-relaxed line-clamp-3">
                      {b.summary}
                    </p>
                    <div className="mt-5 flex items-center justify-between text-white/40 text-xs barlow">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {b.suburb}
                      </span>
                      <span className="inline-flex items-center gap-2 text-cyan-pz bebas tracking-widest">
                        READ
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}
